import React from 'react';

import { Convo, ConvoProps } from './convo';
import { MessageProps } from './message';
import { AnswerDiagnostics } from './answer-diagnostics';
import { DiscardReason } from './discard-form';

import { Accordion } from '@/components/ui/accordion';

// Context types and implementation
interface ConvosContextValue {
  // Fetch functions
  fetchMessages: (conversationId: string) => Promise<MessageProps[]>;
  fetchAttributes: (conversationId: string) => Promise<Record<string, any>[]>;

  // Messages and loading state
  getMessages: (conversationId: string) => MessageProps[];
  isLoading: (conversationId: string) => boolean;
  handleFetchMessages: (conversationId: string) => void;

  // Locale for date formatting
  locale?: string;

  // Discard dialog functions
  fetchAnswerDiagnostics: (messageId: string) => Promise<AnswerDiagnostics>;
  onDiscard: (id: string, reason: DiscardReason, suggestedAnswer?: string) => Promise<void>;
  onApprove: (id: string) => Promise<void>;
}

const ConvosContext = React.createContext<ConvosContextValue | undefined>(undefined);

export function useConvosContext() {
  const context = React.useContext(ConvosContext);
  if (context === undefined) {
    throw new Error('useConvosContext must be used within a ConvosProvider');
  }

  return context;
}

interface ConvosProviderProps {
  children: React.ReactNode;
  value: ConvosContextValue;
}

export function ConvosProvider({ children, value }: ConvosProviderProps) {
  return <ConvosContext.Provider value={value}>{children}</ConvosContext.Provider>;
}

interface ConvosProps {
  convos: ConvoProps[];
  fetchMessages: (conversationId: string) => Promise<MessageProps[]>; // required fetch function for lazy loading
  fetchAttributes: (conversationId: string) => Promise<Record<string, any>[]>; // required fetch function for lazy loading
  fetchAnswerDiagnostics: (messageId: string) => Promise<AnswerDiagnostics>;
  onDiscard: (id: string, reason: DiscardReason, suggestedAnswer?: string) => Promise<void>;
  onApprove: (id: string) => Promise<void>;
}

/**
 * Convos component - Displays a list of conversations in an accordion.
 * Messages are always lazy loaded when accordion items are opened.
 *
 * @param convos - Array of conversation data
 * @param fetchMessages - Async function to fetch messages for a conversation
 */
export function Convos({
  convos,
  fetchMessages,
  fetchAttributes,
  fetchAnswerDiagnostics,
  onDiscard,
  onApprove,
}: ConvosProps) {
  // Maps conversation IDs to their fetched messages
  const [messagesMap, setMessagesMap] = React.useState<Record<string, MessageProps[]>>({});

  // Tracks which conversations are currently loading messages
  const [loadingMap, setLoadingMap] = React.useState<Record<string, boolean>>({});

  // Refs used to check fetch status without causing re-renders
  // These are updated synchronously and don't trigger React re-renders
  const fetchedSetRef = React.useRef<Set<string>>(new Set());
  const loadingMapRef = React.useRef<Record<string, boolean>>({});

  /**
   * Handles fetching messages when an accordion item is clicked.
   * Prevents duplicate fetches by checking refs before making the request.
   * Updates both refs (for synchronous checks) and state (for UI updates).
   */
  const handleFetchMessages = React.useCallback(
    (conversationId: string) => {
      // Skip if already fetched or currently loading
      if (fetchedSetRef.current.has(conversationId) || loadingMapRef.current[conversationId]) {
        return;
      }

      // Set loading state in both ref (for quick checks) and state (for UI)
      loadingMapRef.current[conversationId] = true;
      setLoadingMap((prev) => ({ ...prev, [conversationId]: true }));

      fetchMessages(conversationId)
        .then((fetchedMessages) => {
          // Store fetched messages and mark as fetched
          setMessagesMap((prev) => ({ ...prev, [conversationId]: fetchedMessages }));
          fetchedSetRef.current.add(conversationId);
        })
        .catch(() => {
          // Error handling - could be extended with error reporting
        })
        .finally(() => {
          // Clear loading state from both ref and state
          delete loadingMapRef.current[conversationId];
          setLoadingMap((prev) => {
            const newMap = { ...prev };
            delete newMap[conversationId];

            return newMap;
          });
        });
    },
    [fetchMessages],
  );

  // Create context value - use the first convo's locale as default, or allow override
  const defaultLocale = convos[0]?.locale;

  const contextValue = React.useMemo(
    () => ({
      fetchMessages,
      fetchAttributes,
      getMessages: (conversationId: string) => messagesMap[conversationId] || [],
      isLoading: (conversationId: string) => loadingMap[conversationId] || false,
      handleFetchMessages,
      locale: defaultLocale,
      fetchAnswerDiagnostics: (messageId: string) => fetchAnswerDiagnostics(messageId),
      onDiscard,
      onApprove,
    }),
    [
      fetchMessages,
      fetchAttributes,
      messagesMap,
      loadingMap,
      handleFetchMessages,
      defaultLocale,
      fetchAnswerDiagnostics,
      onDiscard,
      onApprove,
    ],
  );

  return (
    <ConvosProvider value={contextValue}>
      <Accordion type="single" collapsible className="w-full space-y-2">
        {convos.map((convo) => (
          <Convo
            {...convo}
            key={convo.value}
            // Trigger fetch when accordion trigger is clicked
            onClick={() => handleFetchMessages(convo.value)}
          />
        ))}
      </Accordion>
    </ConvosProvider>
  );
}
